import { useEffect, useRef, useState } from 'react';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { useSubscribe as _useSubscribe, useFind as _useFind, useTracker as _useTracker } from 'meteor/react-meteor-data';
import { MethodDeclaration } from '/imports/methods';
import { Publications } from '/imports/publications';
import { Subscriptions } from '/imports/subscriptions';
import { defer, isPublicPath, publicPaths } from '/imports/utils';
import { Accounts, hasConsumptionMeters, hasGenerate, hasProductionMeters, hasProspector, hasUtilityAccounts, isLuminiaUser } from '/imports/api/accounts';
import { clearImpersonation } from '/imports/impersonation';

export const useTracker = _useTracker;

/**
 * Reactive logging-in state.
 * @param deps - tracker dependency list
 * @returns whether a login is in progress
 */
export const useLoggingIn = (deps: any[] = []) => {
  return _useTracker(() => Meteor.loggingIn(), deps);
};

/** Reactive current user, or undefined. */
export const useUser = () => {
  return useTracker(() => Meteor.user() || undefined, []);
};

/** Reactive: is the current user one of Luminia's own (a super user, or the luminia role in their current org)? */
export const useIsLuminia = () => useTracker(() => {
  const user = Meteor.user();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return isLuminiaUser(user, Accounts.findOne({ userId: user._id, orgId: user.orgId }));
}, []);

/** Reactive: may the current user read consumption meters (a super user, or the consumptionMeters role)? */
export const useHasConsumptionMeters = () => useTracker(() => {
  const user = Meteor.user();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasConsumptionMeters(user, Accounts.findOne({ userId: user._id, orgId: user.orgId }));
}, []);

/** Reactive: may the current user read production meters (a super user, or the productionMeters role)? */
export const useHasProductionMeters = () => useTracker(() => {
  const user = Meteor.user();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasProductionMeters(user, Accounts.findOne({ userId: user._id, orgId: user.orgId }));
}, []);

/** Reactive: may the current user read utility accounts (a super user, or the utilityAccounts role)? */
export const useHasUtilityAccounts = () => useTracker(() => {
  const user = Meteor.user();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasUtilityAccounts(user, Accounts.findOne({ userId: user._id, orgId: user.orgId }));
}, []);

/** Reactive: may the current user open the Generate module (see hasGenerate)? */
export const useHasGenerate = () => useTracker(() => {
  const user = Meteor.user();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasGenerate(user, Accounts.findOne({ userId: user._id, orgId: user.orgId }));
}, []);

/** Reactive: may the current user work the Prospector module (a super user, or the prospector role)? */
export const useHasProspector = () => useTracker(() => {
  const user = Meteor.user();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasProspector(user, Accounts.findOne({ userId: user._id, orgId: user.orgId }));
}, []);

/** Redirects to /login (preserving the target path) when no active user resolves. */
export const useLoggedOutRedirect = () => {
  const pathname = window.location.pathname;
  const loggingIn = useLoggingIn();
  const loginTimeout = useRef<NodeJS.Timeout>(undefined);
  const userId = _useTracker(() => Meteor.userId(), []);
  const user = useUser();
  useEffect(() => {
    if (isPublicPath()) return;
    if (loggingIn) return;
    loginTimeout.current && clearTimeout(loginTimeout.current);
    loginTimeout.current = setTimeout(async () => {
      const user = await Meteor.userAsync();
      if (!loggingIn && !user?.active) {
        // Logging out ends an impersonation: the stashed token belongs to a session nobody is in any more.
        clearImpersonation();
        const redirect = publicPaths.includes(pathname) ? '/' : window.btoa(`${pathname}${window.location.search}`);
        window.location.href = `/login?r=${redirect}`;
        return;
      }
    }, 1000);
  }, [userId, loggingIn, user?.active]);
};

export const useFind = _useFind;

/**
 * Reactively finds the first document matching a cursor.
 * @param cursor - factory returning a Mongo cursor
 * @param deps - dependency list
 * @returns the first matching document, or undefined
 */
export const useFindOne = <T extends { _id: string }>(cursor: () => Mongo.Cursor<T>, deps: any[] = []) => {
  return useFind(cursor, deps)[0];
};

/**
 * Resolves a promise into render state.
 * @param promise - the promise to await
 * @param deps - dependency list controlling re-runs
 * @returns tuple of [resolved value, pending, error]
 */
export const usePromise = <T>(promise: Promise<T>, deps: any[] = [promise]) => {
  const [resolved, setResolved] = useState<T>();
  const [error, setError] = useState<Error>();
  const [pending, setPending] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        setPending(true);
        setResolved(await promise);
        defer(() => setPending(false));
      }
      catch (error: any) {
        console.log(error);
        setError(error);
      }
      finally {
        defer(() => setPending(false));
      }
    })();
  }, deps);
  return [resolved, pending, error] as [T | undefined, boolean, Error | undefined];
};

/**
 * Calls a Meteor method and exposes its result as render state.
 * @param method - the method declaration to invoke
 * @param arg - argument passed to the method
 * @param deps - dependency list controlling re-runs
 * @param cond - optional guard; when it returns false the call is skipped
 * @returns tuple of [result, loading, error]
 */
export const useMethod = <A, R>(method: MethodDeclaration<A, R>, arg: A, deps: any[] = [], cond?: (deps: any[]) => boolean) => {
  const result = useRef<R | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const error = useRef<Error | undefined>(undefined);
  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        result.current = cond && !cond(deps) ? undefined : await method.call(arg);
        error.current = undefined;
      }
      catch (error: any) {
        result.current = undefined;
        error.current = error;
      }
      finally {
        setLoading(false);
      }
    })();
  }, deps);
  return [result.current, loading, error.current] as [R | undefined, boolean, any | undefined];
};

/**
 * Builds an in-memory Mongo collection from documents.
 * @param docs - documents to insert
 * @param deps - dependency list controlling rebuilds
 * @returns tuple of [collection, initializing, error]
 */
export const useCollection = <T extends { _id?: string }>(docs: T[], deps: any[] = []) => {
  const [collection, setCollection] = useState<Mongo.Collection<T>>();
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<Error>();
  useEffect(() => {
    (async () => {
      try {
        const collection = new Mongo.Collection<T>(null);
        for (const doc of docs) {
          await collection.insertAsync(doc as any);
        }
        setInitializing(false);
        setCollection(collection);
      }
      catch (error: any) {
        console.log(error);
        setError(error);
      }
      finally {
        setInitializing(false);
      }
    })();
  }, deps);
  return [collection, initializing, error] as [Mongo.Collection<T>, boolean, Error | undefined];
};

/**
 * Subscribes to a publication.
 * @param subName - publication name
 * @param args - subscription arguments
 * @returns a ready() accessor
 */
export const useSubscribe = (subName: keyof typeof Publications | string, ...args: any[]) => {
  return _useSubscribe(subName, ...args);
};

/**
 * Subscribes to a publication and returns its readiness.
 * @param subName - publication name
 * @param args - subscription arguments
 * @returns whether the subscription is ready
 */
export const useSubscription = (subName: keyof typeof Publications | string, ...args: any[]) => {
  return _useSubscribe(subName, ...args)();
};

/**
 * Tracks a registered subscription predicate.
 * @param name - registered subscription key
 * @param deps - extra dependency list
 * @returns the predicate's current value
 */
export const useSubscribed = (name: string, deps: any[] = []) => {
  const sub = Subscriptions[name];
  if (!sub) throw new Error(`useSubscribed: ${name} not registered`);
  const [loading, setLoading] = useState(sub());
  useEffect(() => { setLoading(sub()); }, [sub(), ...deps]);
  return loading;
};

/**
 * Returns the value from the previous render.
 * @param value - current value
 * @returns the prior render's value, or null on first render
 */
export const usePrevious = <T>(value: T) => {
  const ref = useRef<T>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};